import { Link, usePage } from "@inertiajs/react"
import { LogOut, Menu, Settings, Trophy } from "lucide-react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { UserMenuContent } from "@/components/user-menu-content"
import { getInitials } from "@/lib/initials"
import {
  dashboardPath,
  leaguePath,
  settingsProfilePath,
  signOutPath,
} from "@/routes"
import type { Auth, BreadcrumbItem, League } from "@/types"

import { AppLogo } from "./app-logo"

interface AppHeaderProps {
  breadcrumbs?: BreadcrumbItem[]
}

export function AppHeader({ breadcrumbs = [] }: AppHeaderProps) {
  const page = usePage()
  const { auth, leagues } = page.props as { auth: Auth; leagues: League[] }
  return (
    <div className="group/nav bg-card relative border-b-0">
      <div className="mx-auto flex h-16 items-center px-4 md:max-w-7xl">
        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="mr-2 h-[34px] w-[34px]"
              >
                <Menu className="size-5" />
                <span className="sr-only">Toggle navigation</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="bg-sidebar flex h-full w-64 flex-col items-stretch justify-between"
            >
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetHeader className="flex justify-start text-left">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-8 w-7 items-center justify-center [clip-path:polygon(8%_0%,92%_0%,100%_8%,100%_85%,50%_100%,0%_85%,0%_8%)]"
                    style={{
                      background:
                        "linear-gradient(180deg, oklch(0.30 0.10 28), oklch(0.18 0.07 28))",
                    }}
                  >
                    <span
                      className="font-display text-xs leading-none font-bold"
                      style={{ color: "oklch(0.72 0.14 28)" }}
                    >
                      N
                    </span>
                  </div>
                  <span
                    className="font-display text-base font-bold tracking-widest uppercase"
                    style={{
                      color: "var(--accent)",
                      textShadow:
                        "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
                    }}
                  >
                    Nuffle
                  </span>
                </div>
              </SheetHeader>
              <div className="flex h-full flex-1 flex-col space-y-4 p-4">
                <div className="flex h-full flex-col justify-between text-sm">
                  <div className="flex flex-col space-y-4">
                    {leagues.length > 0 && (
                      <div>
                        <div
                          className="mb-3 h-1"
                          style={{
                            background:
                              "repeating-linear-gradient(-45deg, oklch(0.55 0.22 145) 0px, oklch(0.55 0.22 145) 4px, oklch(0.65 0.2 85) 4px, oklch(0.65 0.2 85) 8px)",
                          }}
                        />
                        <p
                          className="font-display mb-2 text-sm font-bold tracking-widest uppercase"
                          style={{
                            color: "var(--accent)",
                            textShadow:
                              "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
                          }}
                        >
                          Your Leagues
                        </p>
                        <div className="flex flex-col space-y-2">
                          {leagues.map((league) => (
                            <Link
                              key={league.id}
                              href={leaguePath(league.id)}
                              className="flex items-center space-x-2 text-sm font-medium"
                            >
                              <Trophy className="size-4 flex-shrink-0" />
                              <span>{league.name}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-2">
                      <div
                        className="mb-3 h-1"
                        style={{
                          background:
                            "repeating-linear-gradient(-45deg, oklch(0.55 0.22 145) 0px, oklch(0.55 0.22 145) 4px, oklch(0.65 0.2 85) 4px, oklch(0.65 0.2 85) 8px)",
                        }}
                      />
                      <div className="flex flex-col space-y-2">
                        <Link
                          href={settingsProfilePath()}
                          className="flex items-center space-x-2 text-sm font-medium"
                        >
                          <Settings className="size-4 flex-shrink-0" />
                          <span>Settings</span>
                        </Link>
                        <Link
                          method="delete"
                          href={signOutPath()}
                          as="button"
                          className="flex items-center space-x-2 text-sm font-medium"
                        >
                          <LogOut className="size-4 flex-shrink-0" />
                          <span>Sign Out</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <Link
          href={dashboardPath()}
          prefetch
          className="flex items-center space-x-2"
        >
          <AppLogo />
        </Link>

        {/* Desktop Inline Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <div className="ml-4 hidden items-center lg:flex">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        )}

        <div className="ml-auto flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="size-10 rounded-full p-1"
                aria-label={auth.user.name}
              >
                <Avatar className="size-8 overflow-hidden rounded-full">
                  <AvatarFallback
                    className="rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)]"
                    style={{ boxShadow: "0 0 0 2px var(--border-heavy)" }}
                  >
                    {getInitials(auth.user.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="relative w-56 border-none bg-[oklch(0.2_0.008_240)] shadow-[0_2px_8px_oklch(0_0_0/0.6)]"
              align="end"
            >
              <UserMenuContent auth={auth} />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <span
        className="animate-gold-breathe pointer-events-none absolute right-0 bottom-0 left-0 h-[3px] [animation-play-state:paused] group-hover/nav:[animation-play-state:running]"
        style={{
          background:
            "linear-gradient(90deg, oklch(0.35 0.06 70), oklch(0.6 0.12 85), oklch(0.8 0.16 85), oklch(0.6 0.12 85), oklch(0.35 0.06 70))",
          opacity: 0.5,
        }}
      />
    </div>
  )
}
